import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

export const Stability = () => {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="chapter-stability">
      <article className="max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <header className="mb-16 animate-fade-in">
          <p className="text-xs font-sans font-medium tracking-widest uppercase text-stone-400 mb-4">
            Chapter
          </p>
          <h1 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl text-stone-800 tracking-tight leading-[1.2]">
            Stability
          </h1>
        </header>

        <div className="prose-living space-y-8 animate-fade-in-delay-1">
          <p>
            Stability isn't about having everything figured out. 
            It's about knowing where to stand when things shift.
          </p>

          <p>
            Some seasons of life feel solid. Others feel uncertain. 
            Both are part of the same journey.
          </p>

          <p>
            This chapter is for moments when you want to take stock. 
            Not to fix anything. Not to solve anything. 
            Just to understand what's underneath — 
            what holds you up when the ground moves.
          </p>

          <div className="py-8 border-t border-b border-stone-100 my-12 space-y-6">
            <h2 className="font-serif text-xl text-stone-700">
              Questions worth sitting with:
            </h2>
            <ul className="space-y-4 text-stone-600">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What parts of your life feel steady right now?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What feels like it's still finding its footing?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>Who or what gives you a sense of ground?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What would help you feel more settled — even a little?</span>
              </li>
            </ul>
          </div>

          <p>
            Stability doesn't mean nothing changes. 
            It means having something to hold onto when it does.
          </p>

          <p>
            This space is for noticing what that might be for you.
          </p>
        </div>

        <nav className="mt-16 pt-8 border-t border-stone-100 animate-fade-in-delay-2">
          <p className="text-sm font-sans text-stone-400 mb-4">Explore other chapters</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/living-book/chapters/youre-not-behind"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-youre-not-behind"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              You're Not Behind
            </Link>
            <Link
              to="/living-book/chapters/transition"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-transition"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Transition
            </Link>
            <Link
              to="/living-book/chapters/longevity"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-longevity"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Longevity
            </Link>
          </div>
        </nav>
      </article>
    </div>
  );
};

export default Stability;
